/**
 * Created by Qingger Corp.
 * User: yangzihua
 * Date: 2019/6/13
 * Time: 13:45
 * Desc: 分销策略设置
 */

import { Component, Vue } from 'vue-property-decorator';
import type { ElForm } from 'element-ui/types/form';
import { quillEditor } from 'vue-quill-editor';
import { IPSService } from '@/services/ipsServices';
import baseAPIService from '@/services/base/baseAPIService';
import NoAuth from '@/views/components/noAuth/noAuth.vue';
import { Functions } from '@/library/utils/functions';

import editorOptionConfig from '@/library/utils/editerOptionConfig'; // 富文本编辑器全局配置
import { appEnvConfig } from '@/configs/appConfigs';
import { EmpServiceAPIActions } from '@/services/empService';

// 子组件

@Component({
  components: {
    quillEditor,
    NoAuth,
  },
})
export default class DistributeStrategyEdit extends Vue {
  /* 数据 */

  /**
   * corpId
   */
  public corpId = baseAPIService.resolveCorpId();

  /**
   * 分销策略详情
   * @type {StrategyInfo}
   */
  public distributeStrategyForm: StrategyInfo = {
    policyName: '',
    deposit: 0,
    commissionProportion: 0,
    discountLimitProportion: '0', // 最终结算价低于商品原价XX%不计算佣金
    commissionTime: 0,
    shareCacheTime: '0',
    outMinAmount: 0,
    outMaxNumber: 0,
    outTotalAmount: 0,
    outContent: '',
    cardWatermark: '',
    shopownerContent: '',
    shopownerAgreement: '',
    guiderShareCacheTime: '',
    secondMaxPerson: 0,
    secondCommissionProportion: 0,
    secondValidityPeriodDay: 0,
    secondVisitPage: '',
    isShowSecond: false,
    secondSharePicture: '',
    isHasOrder: 0,
    orderMinPrice: 0,
    orderDaysAgo: 0,
    isOpenCstInSite: 0,
    isOpenGuider: 0,
    secondShopownerAgreement: '',
  };

  /**
   * 数据效验（有浮点）
   * @type {Function}
   */
  public numberRuleWithFloat = (rule, value, callback) => {
    const reg = /^(\d+(\.\d{1,2})?)$/;
    if (value === '') {
      callback(new Error('不能为空'));
    } else if (!reg.test(value)) {
      callback(new Error('请输入不小于0的整数或最多带2位小数的浮点数'));
    } else {
      callback();
    }
  };

  /**
   * 数据效验（整数）
   * @type {Function}
   */
  public numberRule = (rule, value, callback) => {
    const reg = /^[1-9]\d*$/;
    if (value === '') {
      callback(new Error('不能为空'));
    } else if (!reg.test(value)) {
      callback(new Error('请输入不小于0的整数'));
    } else {
      callback();
    }
  };

  /**
   * 数据效验（整数）
   * @type {Function}
   */
  public numberRule1 = (rule, value, callback) => {
    const reg = /^\d+$/;
    if (value === '') {
      callback(new Error('不能为空'));
    } else if (!reg.test(value)) {
      callback(new Error('请输入不小于0的整数'));
    } else {
      callback();
    }
  };

  /**
   * 数据效验（整数）
   * @type {Function}
   */
  public numberRule4 = (rule, value, callback) => {
    const reg = /^\d+$/;
    if (value === '') {
      callback(new Error('不能为空'));
    } else if (!reg.test(value)) {
      callback(new Error('请输入不小于0的整数'));
    } else if (value > 1000) {
      callback(new Error('请输入不大于1000的整数'));
    } else {
      callback();
    }
  };

  /**
   * 每天可提现次数验证
   * @type {Function}
   */
  public outMaxNumberRule = (rule, value, callback) => {
    const reg = /^[1-9]\d*$/;
    if (value === '') {
      callback(new Error('不能为空'));
    } else if (!reg.test(value)) {
      callback(new Error('请输入不小于0的整数'));
    } else if (value > 10) {
      callback(new Error('请输入不大于10的整数'));
    } else {
      callback();
    }
  };

  /**
   * 起提额度
   * @type {Function}
   */
  public outMinAmountRule = (rule, value, callback) => {
    const reg = /^([1-9]\d*(\.\d{1,2})?)$/;
    if (value === '') {
      callback(new Error('不能为空'));
    } else if (!reg.test(value)) {
      callback(new Error('请输入不小于1的整数或最多带2位小数的浮点数'));
    } else {
      callback();
    }
  };

  /**
   * 每天可提现次金额证
   * @type {Function}
   */
  public outTotalAmountRule = (rule, value, callback) => {
    const reg = /^(\d+(\.\d{1,2})?)$/;
    if (value === '') {
      callback(new Error('不能为空'));
    } else if (!reg.test(value)) {
      callback(new Error('请输入不小于0的整数或最多带2位小数的浮点数'));
    } else if (value > 5000) {
      callback(new Error('请输入不大于5000的整数或最多带2位小数的浮点数'));
    } else {
      callback();
    }
  };

  /**
   * 分销策略详情表单验证规则
   * @type {ItemObject}
   */
  public distributeStrategyFormRules: ItemObject = {
    policyName: [{ required: true, message: '请输入活动名称', trigger: 'blur' }],
    deposit: [{ validator: this.numberRuleWithFloat, trigger: 'blur' }],
    commissionProportion: [{ validator: this.numberRuleWithFloat, trigger: 'blur' }],
    discountLimitProportion: [{ validator: this.numberRuleWithFloat, trigger: 'blur' }],
    commissionTime: [{ validator: this.numberRule, trigger: 'blur' }],
    shareCacheTime: [{ validator: this.numberRule, trigger: 'blur' }],
    guiderShareCacheTime: [{ validator: this.numberRule, trigger: 'blur' }],
    outMinAmount: [{ validator: this.outMinAmountRule, trigger: 'blur' }],
    outMaxNumber: [{ validator: this.outMaxNumberRule, trigger: 'blur' }],
    outTotalAmount: [{ validator: this.outTotalAmountRule, trigger: 'blur' }],
  };

  /**
   * 富文本配置
   * @type {ItemObject}
   */
  public editorOptionConfig: ItemObject = editorOptionConfig();

  /**
   * 图片上传地址
   * @type {string}
   */
  public fileTpConfig = `${Functions.ipsHostUrlApi()}/pic/upload/withOut/key`;

  /**
   * token
   * @type {{Authorization: string}}
   */
  public tokenData: ItemObject = {
    Authorization: `Bearer ${EmpServiceAPIActions.getTokenFromCache().token}`,
  };

  /**
   * 全屏loading
   * @type {boolean}
   */
  public fullscreenLoading = false;

  /**
   * 上传的图标地址
   * @type {string}
   */
  public uploadPic = '';

  /**
   * 当前添加图片的富文本
   * @type {null}
   */
  public addRang: any = {};

  /**
   * 内容是否显示
   * @type {boolean}
   */
  public isContentBodyShow = false;

  /**
   * 表单是否显示
   * @type {boolean}
   */
  public isFormShow = false;

  /**
   * 图片放大弹窗是否显示
   * @type {boolean}
   */
  public isMagnifyPopShow = false;

  /* 方法 */

  /**
   * 如果用户输入的是0.00*，自动转化为0
   * @param {string} item
   * @return null
   */
  zeroIntig(item): void {
    if (Number(this.distributeStrategyForm[item]) === 0) { this.distributeStrategyForm[item] = 0; }
  }

  /**
   * 获取策略信息
   * @return null
   */
  public getStrategyInfo(): void {
    const queryParams = {
      policyId: Number.parseInt(this.$route.params.id),
    };
    IPSService.guiderConfigInfo({ queryParams }).then((res) => {
      res.commissionProportion = res.commissionProportion ? res.commissionProportion * 100 : 0;
      res.discountLimitProportion = res.discountLimitProportion ? res.discountLimitProportion * 100 : 0;
      res.secondCommissionProportion = res.secondCommissionProportion ? res.secondCommissionProportion * 100 : 0;
      res.deposit = res.deposit ? (res.deposit / 100).toFixed(2) : 0;
      res.outMinAmount = res.outMinAmount ? (res.outMinAmount / 100).toFixed(2) : 0;
      res.outTotalAmount = res.outTotalAmount ? (res.outTotalAmount / 100).toFixed(2) : 0;
      // res.isShowSecond = res.isShowSecond;
      this.distributeStrategyForm = res;

      this.distributeStrategyForm = Object.assign({}, this.distributeStrategyForm, {
        isShowSecond: !!this.distributeStrategyForm.isShowSecond,
      });
    });
  }

  /**
   * 返回上页
   * @return null
   */
  public onGoToBack(): void {
    this.$router.go(-1);
    this.clearData();
  }

  /**
   * 保存分销策略
   * @return null
   */
  public onSaveStrategy(): void {
    // 转换格式成数组
    const distributeStrategy = JSON.parse(JSON.stringify(this.distributeStrategyForm));
    distributeStrategy.deposit = Number(distributeStrategy.deposit) * 100;
    distributeStrategy.commissionProportion = Number(distributeStrategy.commissionProportion) / 100;
    distributeStrategy.discountLimitProportion = Number(distributeStrategy.discountLimitProportion) / 100;
    distributeStrategy.commissionTime = Number(distributeStrategy.commissionTime);
    distributeStrategy.secondValidityPeriodDay = Number(distributeStrategy.secondValidityPeriodDay);

    distributeStrategy.secondMaxPerson = Number(distributeStrategy.secondMaxPerson);
    distributeStrategy.outMinAmount = Number(distributeStrategy.outMinAmount) * 100;
    distributeStrategy.outMaxNumber = Number(distributeStrategy.outMaxNumber);
    distributeStrategy.outTotalAmount = Number(distributeStrategy.outTotalAmount) * 100;
    distributeStrategy.secondCommissionProportion = Number(distributeStrategy.secondCommissionProportion) / 100;
    distributeStrategy.isShowSecond = distributeStrategy.isShowSecond ? 1 : 0;
    distributeStrategy.orderDaysAgo = Number(distributeStrategy.orderDaysAgo);
    distributeStrategy.orderMinPrice = Number(distributeStrategy.orderMinPrice);

    if (distributeStrategy.isShowSecond === 0) {
      // 如果未开启二级分销
      // distributeStrategy.secondMaxPerson = 0;
      // distributeStrategy.secondCommissionProportion = 0;
      // distributeStrategy.secondValidityPeriodDay = 0;
      // distributeStrategy.secondVisitPage = '';
      // distributeStrategy.secondSharePicture = '';
      // distributeStrategy.secondShopownerAgreement = '';
    } else {
      if (distributeStrategy.secondCommissionProportion === '') {
        this.$message.error('二级分销佣金比例不能为空');
        return;
      }
      if (distributeStrategy.secondShopownerAgreement === '') {
        this.$message.error('二级用户协议不能为空');
        return;
      }
      if (distributeStrategy.secondValidityPeriodDay > 1000) {
        this.$message.error('下线有效期不能大于1000');
        return;
      }
    }
    const postParams = distributeStrategy;
    (this.$refs.distributeStrategyForm as ElForm).validate((validate) => {
      if (validate) {
        if (this.$route.params.id === '0') {
          // 新增
          IPSService.guiderConfigAdd({ postParams }).then((res) => {
            if (res) {
              this.$message.success('新增成功');
              setTimeout(() => {
                this.onGoToBack();
              }, 1500);
            } else {
              this.$message.success('新增失败');
            }
          });
        } else {
          // 编辑
          postParams.policyId = Number.parseInt(this.$route.params.id);
          IPSService.guiderConfigEdit({ postParams }).then((res) => {
            if (res) {
              this.$message.success('编辑成功');
              setTimeout(() => {
                this.onGoToBack();
              }, 1500);
            } else {
              this.$message.success('编辑失败');
            }
          });
        }
      } else {
        return false;
      }
    });
  }

  /**
   * 是否显示二级分销
   * @return null
   */
  public onIsShowSecondChange(): void {
    (this.$refs.distributeStrategyForm as ElForm).clearValidate();
  }

  /**
   * 图片上传之前(用于富文本编辑器)
   * @param {ItemObject} file
   * @return boolean
   */
  public onBeforeUpload(file: ItemObject): boolean {
    this.fullscreenLoading = true;
    const isJpg = file.type === 'image/jpeg';
    const isPng = file.type === 'image/png';
    const isGif = file.type === 'image/gif';
    const LtSize = file.size / 1024 <= 500;
    if (!isJpg && !isPng && !isGif) {
      this.$message.error('上传图片只能是 JPG/JPEG/PNG/GIF 格式!');
    }
    if (!LtSize) {
      this.$message.error('上传图片大小不能超过 500Kb!');
    }
    if (!((isJpg || isPng || isGif) && LtSize)) { this.fullscreenLoading = false; }
    return (isJpg || isPng || isGif) && LtSize;
  }

  /**
   * 图片上传成功时的处理
   * @param res
   * @param file
   */
  public uploadImgSuccess(res: HttpAPIRetItem<any>, file: any) {
    this.fullscreenLoading = false;
    this.addRang.insertEmbed(this.addRang.getSelection().index, 'image', res.content); // 调用编辑器的 insertEmbed 方法，插入URL
    // 调整光标位置
    this.addRang.getSelection(this.addRang.getSelection().index + 1);
  }

  /**
   * 图片上传失败
   * @return null
   */

  public uploadImgError() {
    this.$message.error('图片上传失败');
    this.fullscreenLoading = false;
  }

  /**
   * 点击图片提现须知ICON触发事件
   * @param {string} quillEditor
   */
  public qillImgHandler(quillName: string): any {
    if (quillName === 'inviteContent' && !this.distributeStrategyForm.isShowSecond) {
      return false;
    }
    const fn = () => {
      this.addRang = (this.$refs[quillName] as any).quill;
      (document.querySelector('#imgInput') as any).click();
    };
    // ts-ignore
    (this.$refs[quillName] as any).quill.getModule('toolbar').addHandler('image', fn);
  }

  /**
   * 清除页面数据
   * @return null
   */
  public clearData(): void {
    this.distributeStrategyForm = {
      policyName: '',
      deposit: 0,
      commissionProportion: 0,
      discountLimitProportion: 0,
      commissionTime: 0,
      shareCacheTime: '',
      guiderShareCacheTime: '',
      outMinAmount: 0,
      outMaxNumber: 0,
      outTotalAmount: 0,
      outContent: '',
      cardWatermark: '',
      shopownerContent: '',
      shopownerAgreement: '',
      secondMaxPerson: 0,
      secondCommissionProportion: 0,
      secondValidityPeriodDay: 0,
      secondVisitPage: '',
      isShowSecond: false,
      secondSharePicture: '',
      isHasOrder: 0,
      orderMinPrice: 0,
      orderDaysAgo: 0,
      isOpenCstInSite: 0,
      isOpenGuider: 0,
      secondShopownerAgreement: '',
    };
  }

  /**
   * 图片上传成功时的处理
   * @return null
   */
  public uploadImageSuccessNormal(res: any, file: any): void {
    this.distributeStrategyForm.secondSharePicture = res.content;
  }

  /**
   * 图片上传失败
   * @return null
   */
  public uploadImgErrorNormal(): void {
    this.$message.error('图片上传失败');
  }

  /**
   * 图片上传之前(普通)
   * @param {ItemObject} file
   * @return boolean
   */
  public onBeforeUploadNormal(file: ItemObject): boolean {
    const isJpg = file.type === 'image/jpeg';
    const isPng = file.type === 'image/png';
    const isGif = file.type === 'image/gif';
    const LtSize = file.size / 1024 <= 300;
    if (!isJpg && !isPng && !isGif) {
      this.$message.error('上传图片只能是 JPG/JPEG/PNG/GIF 格式!');
    }
    if (!LtSize) {
      this.$message.error('上传图片大小不能超过 300Kb!');
    }
    return (isJpg || isPng || isGif) && LtSize;
  }

  /**
   * 删除已上传的图片
   * @return null
   */
  public onDeletePic(): void {
    if (!this.distributeStrategyForm.isShowSecond) {
      return;
    }
    this.distributeStrategyForm.secondSharePicture = '';
  }

  /**
   * 图片放大
   * @return null
   */
  public onMagnifyPic(): void {
    if (!this.distributeStrategyForm.isShowSecond) {
      return;
    }
    this.isMagnifyPopShow = true;
  }
  /* 周期 */

  /**
   * 当组建创建时
   */
  public created() {
    // 权限检测
    this.isContentBodyShow = this.$store.state.authMenuCodeList.includes('VSHOP-FUNCTION-BASIC-DISTRIBUTE');
    if (this.isContentBodyShow) {
      if (this.$route.params.id === '0') {
        // 新增策略
        this.clearData();
      } else {
        this.getStrategyInfo();
      }
    }
  }

  /**
   * 当组件加载完成
   */
  public mounted() {
    // 为图片Icon绑定事件
    this.qillImgHandler('outContent');
    this.qillImgHandler('shopownerContent');
    this.qillImgHandler('shopownerAgreement');
    this.qillImgHandler('visitContent');
    this.qillImgHandler('secondAgreement');
    setTimeout(() => {
      document.querySelector('#distributeStrategyForm').scrollTop = 0;
      this.isFormShow = true;
    }, 300);
  }
}
